import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../services/api';
import { toast } from 'sonner';

const CATEGORIES = ['Restaurant', 'Grocery', 'Pharmacy', 'Cafe', 'Bakery', 'Florist'];

const VendorScrapeImport = ({ prefillUrl = '', autoScan = false, onScanned }) => {
  const nav = useNavigate();
  const [url, setUrl] = useState(prefillUrl);
  const [category, setCategory] = useState('Restaurant');
  const [scraping, setScraping] = useState(false);
  const autoFired = useRef(false);

  const scrape = async (override) => {
    const target = (typeof override === 'string' && override) ? override : url;
    if (!target) { toast.error('Enter your website URL'); return; }
    setScraping(true);
    try {
      const { data } = await api.post('/onboarding/scrape', { url: target, category });
      const n = data.imported_items || 0;
      const secs = data.imported_sections || 0;
      if (n === 0) {
        toast.info("We couldn't auto-detect any products — your store is ready. Add your first item now.");
      } else if (secs > 1) {
        toast.success(`Created ${data.vendor.name} with ${n} items across ${secs} sections. Review prices below.`);
      } else {
        toast.success(`Created ${data.vendor.name} with ${n} item${n === 1 ? '' : 's'}. Edit & save below.`);
      }
      onScanned?.(data);
      nav(data.redirect || '/vendor-admin/menu');
      // Hard reload so AuthContext re-fetches the user's freshly-linked vendor_id.
      setTimeout(() => window.location.reload(), 50);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not scrape the site';
      toast.error(msg, { duration: 6000 });
    } finally { setScraping(false); }
  };

  // Auto-trigger scrape once if invoked with prefillUrl + autoScan (e.g. coming
  // from the Home page CTA or signup-with-url flow). Guarded by a ref so a
  // re-render doesn't re-fire.
  useEffect(() => {
    if (autoScan && prefillUrl && !autoFired.current) {
      autoFired.current = true;
      scrape(prefillUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScan, prefillUrl]);

  return (
    <div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="h-5 w-5 text-emerald-600" />
          <h2 className="font-semibold text-slate-900">Import from your existing website</h2>
        </div>
        <p className="text-sm text-slate-500">Paste your URL — our AI will extract your store name and menu so you don't have to retype everything.</p>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-3">
          <div>
            <Label className="text-xs text-slate-600">Website URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yourstore.com" className="h-11 mt-1" data-testid="scrape-url-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Category</Label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="scrape-category-select">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={() => scrape()} disabled={scraping} className="h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white" data-testid="scrape-go-btn">
              {scraping ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              {scraping ? 'Scanning & creating…' : 'Scan & create store'}
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">
          We'll scan your site, create your ShipitNow store with all extracted products + images, and drop you into the menu editor. A super-admin will approve before sales go live.
        </p>
      </div>
    </div>
  );
};

export default VendorScrapeImport;
